/**
 * DeploymentDetail — S3 Failed-invoice list (SIH-3 / FR-3, slice 1).
 *
 * Reached in ONE click from a portfolio count (pre-filtered to the
 * deployment + period carried in the URL — J1 step 2), keeping the
 * interaction budget ≤3. All designed real states are first-class: loading,
 * empty ("no invoices in period" + scope restated), endpoint error naming
 * the failure, and the invalid custom range (including a bookmarked bad
 * URL) rejected as a real state before any query (FR-5).
 *
 * The header freshness is the branch's row in the TPIN's /branches list, read through
 * useBranchContext - the per-branch summary is retired from the SPA (ADR-005 3.4).
 */

import { useEffect, useMemo } from 'react'
import { routePeriodUsable, useRoute } from '@components/route'
import { AppHeader } from '@components/AppHeader'
import { Breadcrumbs, listCrumbs } from '@components/Breadcrumbs'
import { BranchRefresh } from '@components/BranchRefresh'
import { InvoiceList } from '@components/InvoiceList'
import { Pagination } from '@components/Pagination'
import { branchFreshness } from '@domain/tpinBranch'
import { EmptyState, ErrorState, LoadingState, RefreshingFrame } from '@components/RealStates'
import { useBranchContext } from '@components/useBranchContext'
import { tinOfBranch } from '@domain/tpinPaths'
import { useInvoiceListStore } from '@stores/invoiceListStore'

export function DeploymentDetail({ dbName }: { dbName: string }) {
  const route = useRoute()
  const period = route.period

  const invoiceListView = useInvoiceListStore((s) => s.page)
  const loadInvoices = useInvoiceListStore((s) => s.load)

  const rangeVerdict = routePeriodUsable(period)
  // Memoized on period primitives — stable reference keeps the load effect
  // from re-running on every render.
  const resolved = useMemo(
    () => (rangeVerdict.usable ? rangeVerdict.resolved : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [period.preset, period.from, period.to],
  )

  // An unusable range is the empty string: no real date is empty, so no branch read is made for it.
  const from = resolved === null ? '' : resolved.from
  const to = resolved === null ? from : resolved.to
  const branch = useBranchContext(tinOfBranch(dbName), dbName, from, to)

  useEffect(() => {
    if (resolved === null) return
    void loadInvoices(dbName, resolved)
    // Re-query on any URL scope change; never serve the previous scope's rows.
  }, [dbName, loadInvoices, resolved])

  const page = invoiceListView.kind === 'ready' ? invoiceListView.data : invoiceListView.kind === 'refreshing' ? invoiceListView.previous : null

  const scopeText = `Deployment ${dbName}, invoices created ${
    resolved !== null && resolved.from === resolved.to ? `on ${resolved.from}` : `between ${resolved?.from} and ${resolved?.to}`
  }`

  const handleRefresh = () => {
    if (resolved === null) return
    branch.refresh()
    void loadInvoices(dbName, resolved)
  }

  return (
    <div className="page page-deployment">
      <AppHeader period={period} />
      <main className="page-main">
        <BranchRefresh
          freshness={branchFreshness(branch.lookup)}
          refreshing={invoiceListView.kind === 'refreshing' || branch.refreshing}
          onRefresh={handleRefresh}
        />

        {/* Breadcrumb, then the title: no period selector here - the period is set on the branch view (PD-36 N1). */}
        <Breadcrumbs crumbs={listCrumbs(dbName, 'Failed invoices')} period={period} />
        <h1>Failed invoices — {dbName}</h1>

        {/* The period travels in the URL from the branch view; this list does not change it. */}

        {!rangeVerdict.usable ? (
          // Invalid custom range from a bookmarked/shared URL: real error
          // state, never queried (FR-5).
          <ErrorState message={rangeVerdict.reason} />
        ) : (
          <RefreshingFrame refreshing={invoiceListView.kind === 'refreshing'}>
            {invoiceListView.kind === 'loading' && <LoadingState label={`Loading failed invoices for ${dbName}…`} />}
            {invoiceListView.kind === 'error' && <ErrorState message={invoiceListView.message} onRetry={() => resolved !== null && void loadInvoices(dbName, resolved)} />}
            {(invoiceListView.kind === 'ready' || invoiceListView.kind === 'refreshing') && page !== null && (
              <>
                {page.invoices.length === 0 ? (
                  <EmptyState scope={scopeText} />
                ) : (
                  <>
                    <InvoiceList rows={page.invoices} dbName={dbName} />
                    <Pagination page={page} onCursor={(cursor) => resolved !== null && void loadInvoices(dbName, resolved, { cursor })} />
                  </>
                )}
              </>
            )}
          </RefreshingFrame>
        )}
      </main>
    </div>
  )
}
